import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDateTime, formatMoney, formatNumber, intlLocale } from "../../lib/format";
import { TableSkeleton } from "../../components/Loading";
import { ContractOverview } from "./ContractOverview";
import { useTranslation } from "react-i18next";
import { adminApi, type Company } from "../../api/admin";
import { contractsApi, type Contract, type ContractRedUgovora, type ContractStatus, type ContractVersion } from "../../api/contracts";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { useAuth } from "../../auth/AuthContext";
import { Select } from "../../components/Select";
import { ErpReadOnlyNote } from "../../components/ErpReadOnlyNote";
import { CONTRACT_STATUS_KEY, StatusBadge } from "../ContractsPage";
import type { DocumentTemplateOption } from "../../api/documentTemplates";

const ALL_STATUSES: ContractStatus[] = [
  "DRAFT", "SENT_TO_CUSTOMER", "CUSTOMER_VIEWED", "SIGNED", "ACTIVE", "EXPIRED", "CANCELLED",
];

export default function AdminContractsPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("section:sales_contracts:write");
  const { t } = useTranslation();
  const toast = useToast();
  const [contracts, setContracts] = useState<Contract[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [statusFilter, setStatusFilter] = useState<ContractStatus | "">("");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  // Brojevi po ugovoru dolaze iz pregleda, pa spisak i zaglavlje računaju istu stvar na istom
  // mestu i ne mogu da se razmimoiđu.
  const [brojevi, setBrojevi] = useState<Map<string, ContractRedUgovora>>(new Map());
  const [pretraga, setPretraga] = useState("");
  const [kolona, setKolona] = useState<"referenca" | "kupac" | "ugovoreno" | "preostalo" | "rok">("rok");
  const [opadajuce, setOpadajuce] = useState(false);

  // Ugovor bez roka ide na dno, bez obzira na smer: nema datum, pa nije ni najbliži ni najdalji.
  const vidljivi = useMemo(() => {
    const q = pretraga.trim().toLowerCase();
    const f = (contracts ?? []).filter((c) =>
      !q
      || c.referenceNumber.toLowerCase().includes(q)
      || c.title.toLowerCase().includes(q)
      || c.companyName.toLowerCase().includes(q));
    const broj = (c: Contract) => {
      const r = brojevi.get(c.id);
      if (kolona === "ugovoreno") return r?.ugovorenaVrednost ?? null;
      if (kolona === "preostalo") {
        if (!r || r.ugovorenaVrednost == null || r.isporucenaVrednost == null) return null;
        return Math.max(0, r.ugovorenaVrednost - r.isporucenaVrednost);
      }
      return r?.danaDoIsteka ?? null;
    };
    return [...f].sort((a, b) => {
      if (kolona === "referenca" || kolona === "kupac") {
        const x = kolona === "referenca" ? a.referenceNumber : a.companyName;
        const y = kolona === "referenca" ? b.referenceNumber : b.companyName;
        const c = x.localeCompare(y);
        return opadajuce ? -c : c;
      }
      const x = broj(a); const y = broj(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return opadajuce ? y - x : x - y;
    });
  }, [contracts, brojevi, pretraga, kolona, opadajuce]);

  const sortiraj = (k: typeof kolona) => {
    if (k === kolona) setOpadajuce((o) => !o);
    else { setKolona(k); setOpadajuce(k === "ugovoreno" || k === "preostalo"); }
  };
  const glava = (k: typeof kolona, naslov: string, desno = false) => (
    <th style={{ ...th, textAlign: desno ? "right" : "left", cursor: "pointer", userSelect: "none" }}
        onClick={() => sortiraj(k)}
        aria-sort={kolona === k ? (opadajuce ? "descending" : "ascending") : "none"}>
      {naslov}{kolona === k ? (opadajuce ? " ↓" : " ↑") : ""}
    </th>
  );
  useEffect(() => {
    contractsApi.overview(new Date().getFullYear())
      .then((p) => setBrojevi(new Map(p.poUgovoru.map((r) => [r.ugovorId, r]))))
      .catch(() => setBrojevi(new Map()));
  }, []);
  const [openContract, setOpenContract] = useState<Contract | null>(null);

  async function load() {
    try {
      const [list, comps] = await Promise.all([
        contractsApi.list({ status: statusFilter || undefined, companyId: companyFilter || undefined }),
        hasPermission("section:companies:read") ? adminApi.listCompanies(0, 200).then((p) => p.content.filter(Boolean)) : Promise.resolve([]),
      ]);
      setContracts(list);
      setCompanies(comps);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  useEffect(() => { void load(); }, [statusFilter, companyFilter]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.contracts.title")}</h1>
        {canWrite && <Link to="/admin/contracts/new" style={{ ...primary, textDecoration: "none", display: "inline-block" }}>{t("admin.contracts.newContract")}</Link>}
      </header>

      {/* Komercijalni pregled iznad spiska: spisak kaže ko je potpisao i do kada, ovo kaže koliko
          smo vezali i koliko je od toga otišlo. */}
      <ContractOverview />

      <div style={{ display: "flex", gap: 12 }}>
        <Select<string>
          value={companyFilter}
          onChange={setCompanyFilter}
          ariaLabel={t("admin.contracts.allCompanies")}
          style={{ flex: 1 }}
          options={[
            { value: "", label: t("admin.contracts.allCompanies") },
            ...companies.map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <Select<ContractStatus | "">
          value={statusFilter}
          onChange={setStatusFilter}
          ariaLabel={t("admin.contracts.allStatuses")}
          style={{ flex: 1 }}
          options={[
            { value: "", label: t("admin.contracts.allStatuses") },
            ...ALL_STATUSES.map((s) => ({ value: s, label: t(CONTRACT_STATUS_KEY[s] ?? "contracts.status.unknown", s) })),
          ]}
        />
      </div>

      {/* Pretraga ide preko broja, naslova i kupca — tri stvari sa kojima covek dolazi.
          Sortiranje je u pregledacu, jer spisak staje u jedan upit. */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <input
          className="input"
          value={pretraga}
          onChange={(e) => setPretraga(e.target.value)}
          placeholder={t("admin.contracts.search", "Broj ugovora, predmet ili kupac…")}
          aria-label={t("admin.contracts.search", "Broj ugovora, predmet ili kupac…")}
          style={{ flex: 1, minWidth: 220, maxWidth: 420 }}
        />
        {contracts && (
          <span style={{ fontSize: 12.5, color: "var(--ink-3)", marginLeft: "auto" }}>
            {t("admin.contracts.shown", "{{n}} od {{ukupno}}")
              .replace("{{n}}", String(vidljivi.length))
              .replace("{{ukupno}}", String(contracts.length))}
          </span>
        )}
      </div>

      {!contracts ? (
        <TableSkeleton rows={6} cols={5} label={t("common.loading")} />
      ) : vidljivi.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.contracts.empty")}</p>
      ) : (
        <table style={tbl}>
          <thead>
            <tr>
              {glava("referenca", t("admin.contracts.col.reference"))}
              <th style={th}>{t("admin.contracts.col.title")}</th>
              {glava("kupac", t("admin.contracts.col.company"))}
              <th style={th}>{t("admin.contracts.col.status")}</th>
              {glava("ugovoreno", t("admin.contracts.col.contracted", "Ugovoreno"), true)}
              {glava("preostalo", t("admin.contracts.col.remaining", "Preostalo"), true)}
              {glava("rok", t("admin.contracts.col.deadline", "Rok"), true)}
              <th style={th}>{t("admin.contracts.col.versions")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {vidljivi.map((c) => (
              <tr key={c.id}>
                <td style={td}><code>{c.referenceNumber}</code></td>
                <td style={td}>{c.title}</td>
                <td style={td}>{c.companyName}</td>
                <td style={td}><StatusBadge status={c.status} /></td>
                {/* Brojevi ugovora stoje u redu, jer spisak bez njih kaže samo ko je potpisao. */}
                <UgovorBrojevi red={brojevi.get(c.id)} />
                <td style={td}>{c.versionCount}</td>
                <td style={td}>
                  <button type="button" onClick={() => setOpenContract(c)} style={linkBtn}>{t("admin.contracts.action.open")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {openContract && (
        <ContractDetailModal
          contract={openContract}
          onClose={() => setOpenContract(null)}
          onChanged={async () => { await load(); }}
        />
      )}
    </div>
  );
}

function ContractDetailModal({
  contract, onClose, onChanged,
}: {
  contract: Contract;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  // A contract from the ERP is shown here and changed only there: no new versions, no status moves.
  const canWrite = hasPermission("section:sales_contracts:write") && !contract.fromErp;
  const toast = useToast();
  const [versions, setVersions] = useState<ContractVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [markSigned, setMarkSigned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState<DocumentTemplateOption[]>([]);
  const [template, setTemplate] = useState("");

  useEffect(() => {
    if (!canWrite) return;
    contractsApi.templates()
      .then((list) => { setTemplates(list); setTemplate((cur) => cur || list[0]?.id || ""); })
      .catch(() => undefined);
  }, [canWrite]);

  async function load() {
    setLoading(true);
    try {
      setVersions(await contractsApi.versions(contract.id));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, [contract.id]);

  async function upload(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      await contractsApi.uploadVersion(contract.id, file, markSigned);
      toast.success(t("admin.contracts.toast.versionUploaded"));
      setFile(null);
      setMarkSigned(false);
      await load();
      await onChanged();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  /** The contract in Word from a template of the gallery: kept as the next version and downloaded at once. */
  async function fromTemplate() {
    if (!template) return;
    setBusy(true);
    try {
      const v = await contractsApi.versionFromTemplate(contract.id, template);
      toast.success(t("admin.contracts.toast.versionFromTemplate", { n: v.versionNo, defaultValue: "Word made from the template and kept as version {{n}}." }));
      await load();
      const r = await contractsApi.presign(contract.id, v.id);
      const a = document.createElement("a");
      a.href = r.url;
      a.rel = "noopener";
      a.click();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function transition(to: ContractStatus) {
    setBusy(true);
    try {
      await contractsApi.transition(contract.id, to);
      toast.success(t("admin.contracts.toast.movedTo", { status: t(CONTRACT_STATUS_KEY[to] ?? "contracts.status.unknown", to) }));
      await onChanged();
      onClose();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function downloadVersion(v: ContractVersion) {
    try {
      const r = await contractsApi.presign(contract.id, v.id);
      window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  // Stavljanje na snagu je odobrenje, i ono je direktorovo: dugme se ne nudi onome ko bi na njemu
  // samo dobio odbijenicu sa servera.
  const mozeDaAktivira = hasPermission("contracts:approve");
  const allowed = canWrite ? nextAllowed(contract.status).filter((t) => (t !== "ACTIVE" || mozeDaAktivira) && (t !== "CANCELLED" || hasPermission("section:sales_contracts:full"))) : [];

  return (
    <Modal onClose={onClose}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>
          {contract.referenceNumber} — {contract.title}
        </h2>
        <StatusBadge status={contract.status} />
      </header>
      <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
        {contract.companyName}
        {contract.erpDocumentNumber && (
          <> · {t("admin.contracts.erpNumber", "ERP broj")} <code>{contract.erpDocumentNumber}</code></>
        )}
      </p>
      {contract.previousReferenceNumber && (
        <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
          {t("admin.contracts.previous", "Nastao iz ugovora")} <code>{contract.previousReferenceNumber}</code>
        </p>
      )}
      {contract.fromErp && <ErpReadOnlyNote text={t("admin.contracts.fromErp")} />}

      <section>
        <h3 style={subTitle}>{t("admin.contracts.versions.title")}</h3>
        {loading ? (
          <TableSkeleton rows={6} cols={5} label={t("common.loading")} />
        ) : versions.length === 0 ? (
          <p style={{ color: "var(--ink-4)", fontSize: 13 }}>{t("admin.contracts.versions.empty")}</p>
        ) : (
          <table style={miniTbl}>
            <thead>
              <tr>
                <th style={th}>{t("admin.contracts.versions.col.number")}</th>
                <th style={th}>{t("admin.contracts.versions.col.filename")}</th>
                <th style={th}>{t("admin.contracts.versions.col.size")}</th>
                <th style={th}>{t("admin.contracts.versions.col.signed")}</th>
                <th style={th}>{t("admin.contracts.versions.col.uploaded")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <td style={td}>v{v.versionNo}</td>
                  <td style={td}>{v.filename}</td>
                  <td style={td}>{(v.sizeBytes / 1024).toFixed(0)} KB</td>
                  <td style={td}>{v.signed ? t("admin.contracts.versions.signedYes") : "—"}</td>
                  <td style={td}>{formatDateTime(v.uploadedAt)}</td>
                  <td style={td}>
                    <button type="button" onClick={() => downloadVersion(v)} style={linkBtn}>{t("admin.contracts.action.download")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canWrite && templates.length > 0 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
            <Select<string>
              value={template}
              onChange={setTemplate}
              ariaLabel={t("admin.contracts.template", "Template")}
              options={templates.map((x) => ({ value: x.id, label: x.name }))}
              style={{ minWidth: 240, flex: "1 1 240px" }}
            />
            <button type="button" onClick={() => void fromTemplate()} disabled={!template || busy} style={primary}>
              {t("admin.contracts.action.fromTemplate", "Make from template")}
            </button>
          </div>
        )}

        {canWrite && <form onSubmit={upload} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          {/* The platform's own button instead of the browser's, which speaks the browser's language. */}
          <label style={{ ...transitionBtn, display: "inline-flex", alignItems: "center" }}>
            {t("admin.contracts.action.choosePdf", "Choose PDF")}
            <input
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <span style={{ fontSize: 13, color: file ? "var(--ink-2)" : "var(--ink-4)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {file ? file.name : t("admin.contracts.noFile", "No file chosen")}
          </span>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={markSigned} onChange={(e) => setMarkSigned(e.target.checked)} />
            {t("admin.contracts.action.markSigned")}
          </label>
          <button type="submit" disabled={!file || busy} style={primary}>{busy ? t("admin.contracts.action.uploading") : t("admin.contracts.action.upload")}</button>
        </form>}
      </section>

      {allowed.length > 0 && (
        <section>
          <h3 style={subTitle}>{t("admin.contracts.lifecycle")}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {allowed.map((to) => (
              <button type="button" key={to} onClick={() => transition(to)} disabled={busy} style={transitionBtn}>
                {t("admin.contracts.transitionTo", { status: t(CONTRACT_STATUS_KEY[to] ?? "contracts.status.unknown", to) })}
              </button>
            ))}
          </div>
        </section>
      )}
    </Modal>
  );
}

function nextAllowed(from: ContractStatus): ContractStatus[] {
  switch (from) {
    case "DRAFT":            return ["SENT_TO_CUSTOMER", "CANCELLED"];
    case "SENT_TO_CUSTOMER": return ["CUSTOMER_VIEWED", "CANCELLED"];
    case "CUSTOMER_VIEWED":  return ["SIGNED", "CANCELLED"];
    case "SIGNED":           return ["ACTIVE", "CANCELLED"];
    case "ACTIVE":           return ["EXPIRED", "CANCELLED"];
    default:                 return [];
  }
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>{children}</div>
    </div>
  );
}



const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-1)" };
const miniTbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 8 };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const transitionBtn: React.CSSProperties = { padding: "6px 12px", background: "var(--tone-indigo-bg)", color: "var(--tone-indigo)", border: "1px solid var(--tone-indigo)", borderRadius: 6, fontSize: 12, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", fontSize: 12 };
const subTitle: React.CSSProperties = { margin: "16px 0 4px", fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 720, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14, maxHeight: "90vh", overflowY: "auto" };

/**
 * Ugovorena vrednost, preostalo i rok, za jedan red spiska.
 *
 * <p>When the customer has more contracts for the same article, what was invoiced fills them in the
 * order they start, so the remaining figure is this contract's own and a note says it is shared.
 */
function UgovorBrojevi({ red }: { red?: ContractRedUgovora }) {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  if (!red) return (<><td style={td} /><td style={td} /><td style={td} /></>);

  const novac = (n: number | null) =>
    n == null ? "—" : formatMoney(n, "EUR", locale, { maximumFractionDigits: 0 });
  const deljeno = red.deliSa > 1;
  const dana = red.danaDoIsteka;

  return (
    <>
      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        <div>{novac(red.ugovorenaVrednost)}</div>
        {red.ugovorenaKolicina != null && (
          <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>
            {formatNumber(red.ugovorenaKolicina, locale, { maximumFractionDigits: 0 })} kg
          </div>
        )}
      </td>
      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        <div>{novac(red.ugovorenaVrednost == null || red.isporucenaVrednost == null
          ? null
          : Math.max(0, red.ugovorenaVrednost - red.isporucenaVrednost))}</div>
        {red.realizacijaPct != null && (
          <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>
            {formatNumber(red.realizacijaPct, locale, { maximumFractionDigits: 0 })}%
          </div>
        )}
        {deljeno && (
          <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
            {t("admin.contracts.sharedWith", "deli sa {{count}} ugovora", { count: red.deliSa })}
          </div>
        )}
      </td>
      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {dana == null ? "—" : dana < 0 ? (
          <span style={{ color: "var(--ink-4)" }}>{t("admin.contracts.expired", "isteklo")}</span>
        ) : (
          // Trideset dana je prag na kom se o ugovoru još može nešto uraditi.
          <span style={{ color: dana <= 30 ? "var(--warn)" : "var(--ink-2)" }}>
            {t("admin.contracts.daysLeft", "{{count}} dana", { count: dana })}
          </span>
        )}
      </td>
    </>
  );
}
